import styled from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import {
    Theme,
    breakpoint,
    calcRem,
    colors,
    fluidTypography,
    themedValue,
    transition,
    breakpointMax,
} from '../../../..'
import { zIndex } from '../../../../__styling/settings/z-index'
import { TextLink } from '../../../../typography/TextLink/TextLink'
import {
    BreachWallContentType,
    BreachWallStyle,
} from '../../helpers/BreachWallTypes'

export interface BreachTypeProps {
    breachType?: BreachWallContentType
    useGridLayout?: boolean
    numberOfColumns?: number
}

export const StyledBreachWallContent = styled('div')<{
    backgroundColor?: string
}>(({ theme, backgroundColor }) => ({
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
    zIndex: zIndex.common.regWall.box,
    fontFamily: theme.fonts.subscriber.text,
    fontSize: calcRem(18),
    color: colors.white,
    padding: calcRem(theme.margins.md, 0),
    backgroundBlendMode: 'overlay, normal',

    backgroundColor:
        backgroundColor ||
        themedValue(theme, {
            thewest: theme.sectionValues.primaryColor,
            perthnow: colors.perthnow.greyBasalt90,
            sevennews: colors.sevennews.charade,
            fallback: undefined,
        }),
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% auto',
    backgroundPosition: 'top',
    [breakpoint('xxs')]: {
        paddingTop: calcRem(theme.margins.lg),
        paddingBottom: calcRem(theme.margins.md),
    },

    [breakpoint('md')]: {
        backgroundPosition: 'right',
        backgroundSize: 'cover',
    },
}))

interface StyledFullWidthBreachWallBackgroundProps extends BreachTypeProps {
    isFullWidthBelowDesktop?: boolean
}

export const BreachWallWrapper = styled(
    'div',
)<StyledFullWidthBreachWallBackgroundProps>(
    ({ isFullWidthBelowDesktop, breachType }) => [
        isFullWidthBelowDesktop && {
            marginLeft: calcRem(-15),
            marginRight: calcRem(-15),
            [breakpoint('md')]: {
                marginLeft: calcRem(0),
                marginRight: calcRem(0),
            },
        },
        // PROMO: EOFY
        breachType === 'subs-campaign-eofy-weekend-digital' && {
            marginTop: calcRem(24),
        },
    ],
)

interface StyledBreachWallContent2022Props {
    largeImage?: string
    smallImage?: string
    breachStyle?: BreachWallStyle
    isGuernseyEnabled?: boolean
}
export const StyledBreachWallContent2022 = styled(
    StyledBreachWallContent,
)<StyledBreachWallContent2022Props>(
    ({ largeImage, smallImage, breachStyle, isGuernseyEnabled }) => [
        {
            padding: calcRem(120, 24, 24, 24),
            flexDirection: 'row',
            justifyContent: 'center',

            [breakpoint('xxs')]: {
                padding: calcRem(30, 24, 24, 18),
            },
        },
        // Check if the small image or large image is defined
        smallImage && {
            backgroundBlendMode: 'revert',
            backgroundImage: `url('${smallImage}')`,
            backgroundSize: '100% auto',
            overflow: 'hidden',
        },
        largeImage && {
            [breakpoint('sm')]: {
                backgroundImage: `url('${largeImage}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'right',
            },
        },
        // DEFAULT: New 2024 Breachwall Design
        breachStyle === 'default-breachwall-2024' && {
            padding: calcRem(18, 24, 24, 18),
            gap: calcRem(18),

            [breakpoint('xxs')]: {
                padding: calcRem(30, 24, 64, 18),
            },
        },
        // PROMO: EOFY
        breachStyle === 'eofy' && {
            backgroundSize: 'cover',
            padding: calcRem(30, 24, 24, 24),
            [breakpoint('xxs')]: {
                backgroundImage: `${
                    largeImage ? `url('${largeImage}')` : undefined
                }`,
            },
        },
        // PROMO: Vanishing Cousins
        breachStyle === 'vanishing-cousins' && {
            padding: calcRem(30, 24, 24, 24),
            backgroundSize: 'cover',
        },
        // PROMO: Footy Lives Here
        breachStyle === 'footy-lives-here' && {
            padding: calcRem(48, 0),
            backgroundColor: '#000',
            background: `#000, ${
                smallImage ? `url('${smallImage}')` : undefined
            }`,
            backgroundSize: '100%, auto',
            backgroundPosition: 'top',
            backgroundRepeat: 'no-repeat',
            marginTop: calcRem(36),
            gap: calcRem(48),

            // Need to override largeImage styles at this breakpoint or it uses the wrong image
            [breakpoint('sm')]: {
                backgroundImage: `url('${
                    isGuernseyEnabled ? smallImage : largeImage
                }')`,
                backgroundSize: isGuernseyEnabled ? '100%' : '120%',
                backgroundPosition: 'top',
            },

            [breakpoint('lg')]: {
                padding: calcRem(48, 0),
                marginTop: 0,
                backgroundImage: `${
                    largeImage ? `url('${largeImage}')` : undefined
                }`,
                backgroundPosition: 'top',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '100%',
            },
        },
        // PROMO: Black Friday
        breachStyle === 'black-friday' && {
            padding: calcRem(30, 24, 24, 24),
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            [breakpoint('sm')]: {
                backgroundPosition: 'center',
            },
        },
        // PROMO: Digital Radio and Papers
        breachStyle === 'digital-radio-and-papers' && {
            padding: calcRem(24, 16),
            gap: calcRem(16),

            [breakpoint('sm')]: {
                gap: calcRem(32),
                padding: calcRem(40, 62),
            },
        },
        // PROMO: Summer of Cricket:
        breachStyle === 'summer-of-cricket' && {
            backgroundSize: 'cover',
            backgroundPosition: 'top',

            padding: calcRem(24, 16),
            gap: calcRem(16),

            [breakpoint('sm')]: {
                backgroundSize: 'cover',
                backgroundPosition: 'top',

                gap: 0,
                padding: calcRem(32, 0, 16, 0),
            },
        },
    ],
)

export const StyledRegistrationInfo = styled('div')<{
    breachStyle?: BreachWallStyle
    isGuernseyEnabled?: boolean
}>(({ breachStyle, isGuernseyEnabled }) => [
    {
        width: '100%',
        display: 'block',
        textAlign: 'center',
    },
    // PROMO: Footy Lives Here
    breachStyle === 'footy-lives-here' && {
        textAlign: isGuernseyEnabled ? 'center' : 'left',
    },
])

export const StyledTerms2022 = styled.span<BreachTypeProps>(
    ({ theme, breachType }) => [
        {
            flex: '0 1 100%',
            border: 'none',
            borderRadius: calcRem(5),
            fontSize: calcRem(16),
            textAlign: 'center',
            textTransform: 'initial',
            justifySelf: 'center',
            width: '100%',
            padding: calcRem(16),

            backgroundColor: theme.colors.background.primary,
            color: theme.colors.text.primary,
            fontFamily: theme.fonts.serif,

            [breakpoint('sm')]: {
                fontSize: calcRem(16),
            },

            '& a': {
                color: tokens.thewest.colors.actions.link.default,
            },
        },
        // COMBINED: Vanishing Cousins, Black Friday, EOFY, AFL, Summer of Cricket
        (breachType === 'vanishing-cousins' ||
            breachType === 'black-friday' ||
            breachType === 'afl-footy-lives-here-breach-screen' ||
            breachType === 'subs-campaign-eofy-weekend-digital' ||
            breachType === 'summer-of-cricket') && {
            background: 'none',
            color: tokens.thewest.colors.palette.white,

            a: {
                color: tokens.thewest.colors.palette.white,
            },
        },
        // PROMO: Annual Package
        breachType === 'default-annual-package' && {
            maxWidth: calcRem(668),
        },
        // PROMO: Footy Lives Here
        breachType === 'afl-footy-lives-here-breach-screen' && {
            background: 'none',
            fontFamily: theme.fonts.sansSerif,
            padding: 0,
        },
        // PROMO: Digital Radio Package
        breachType === 'digital-radio-and-papers' && {
            padding: 0,
            background: 'none',
            color: tokens.thewest.colors.palette.black,
            a: {
                color: tokens.thewest.colors.palette.black,
            },

            alignSelf: 'center',
            width: '90%',

            [breakpoint('sm')]: {
                width: '100%',
            },
        },
    ],
)

const fluidTitle = (theme: Theme) =>
    themedValue(theme, {
        sevennews: fluidTypography(0, 1000, 24, 30),
        perthnow: fluidTypography(0, 1020, 24, 30),
        thewest: fluidTypography(0, 1220, 48, 56),
        fallback: undefined,
    })

export const StyledTitle = styled('h2')<{ breachType?: BreachWallContentType }>(
    ({ theme, breachType }) => [
        {
            position: 'relative',
            margin: calcRem(theme.margins.sm),
            fontWeight: 700,
            textAlign: 'center',
            lineHeight: 1.267,
            '&.sub-title': {
                fontSize: calcRem(67),
            },

            ...fluidTitle(theme),

            [breakpoint('xs')]: {
                margin: calcRem(0, 0, theme.margins.sm, 0),
            },
        },
        // PROMO: Black Friday
        breachType === 'black-friday' && {
            maxWidth: calcRem(500),
            fontWeight: 400,
            fontSize: calcRem(28),
            color: tokens.thewest.colors.palette.white,
            margin: '0 auto',

            [breakpoint('xs')]: {
                fontSize: calcRem(40),
                margin: '0 auto',
                marginBottom: calcRem(theme.margins.lg),
            },
        },
        // PROMO: Annual Package OR Digital Radio Package
        (breachType === 'default-annual-package' ||
            breachType === 'digital-radio-and-papers') && {
            maxWidth: calcRem(500),
            fontWeight: 400,
            fontSize: calcRem(28),
            color: tokens.thewest.colors.palette.black,
            margin: 0,

            [breakpoint('xs')]: {
                fontSize: calcRem(40),
                marginBottom: calcRem(theme.margins.lg),
            },
        },
        // PROMO: Digital Radio Package
        breachType === 'digital-radio-and-papers' && {
            color: colors.thewest.lightBlack,
            fontFamily: theme.fonts.heading,
            justifySelf: 'center',
            fontWeight: 500,
            fontSize: calcRem(20),
            lineHeight: calcRem(28),
            width: '90%',

            [breakpoint('xs')]: {
                width: '100%',
                fontSize: calcRem(32),
                lineHeight: calcRem(40),
                marginBottom: 0,
            },
        },
    ],
)

export const StyledTitleImage = styled('img')<{
    breachType?: BreachWallContentType
    isGuernseyEnabled?: boolean
}>(({ breachType, isGuernseyEnabled }) => [
    // Base styles for all packages
    {
        width: '100%',
        padding: calcRem(32, 0, 0),

        [breakpoint('xs')]: {
            padding: calcRem(32),
            width: '100%',
            maxWidth: 'none',
        },
    },
    // PROMO: Footy Lives Here
    breachType === 'afl-footy-lives-here-breach-screen' && {
        padding: 0,
        width: isGuernseyEnabled ? '100%' : '60%',

        [breakpoint('xs')]: {
            padding: 0,
            paddingLeft: isGuernseyEnabled ? 0 : calcRem(100),
            maxWidth: calcRem(500),
        },

        [breakpoint('lg')]: {
            padding: 0,
            width: '100%',
        },
    },
    // PROMO: Black Friday
    breachType === 'black-friday' && {
        [breakpoint('xs')]: {
            width: '90%',
            maxWidth: calcRem(800),
        },
    },
    // PROMO: Summer of Cricket
    breachType === 'summer-of-cricket' && {
        padding: calcRem(32, 0, 16),

        [breakpoint('xs')]: {
            padding: calcRem(24, 32, 64),
            width: '90%',
        },
    },
])

export const StyledFooterImage = styled('img')(() => ({
    width: '100%',
    maxWidth: calcRem(240),
    margin: calcRem(48, 70, 0, 70),

    [breakpointMax('xs')]: {
        width: 'Calc(100% - 50px)',
    },
}))

export const StyledMessage = styled('div')<BreachTypeProps>(
    ({ theme, breachType }) => [
        {
            margin: 'auto',
            marginBottom: calcRem(theme.margins.sm),
            maxWidth: calcRem(600),

            p: {
                margin: calcRem(0, theme.margins.sm),
                fontSize: calcRem(20),
                fontWeight: 'normal',
                textAlign: 'center',

                '&.full-width': {
                    fontWeight: 'normal',
                    width: '100%',
                },
            },
        },
        // PROMO: Vanishing Cousins
        breachType === 'vanishing-cousins' && {
            maxWidth: '100%',
            fontFamily: 'futura-pt, sans-serif',

            p: {
                margin: 'auto',
                marginBottom: calcRem(14),
                fontSize: calcRem(24),
                fontWeight: 400,
                letterSpacing: calcRem(4),

                '&.full-width': {
                    fontWeight: 100,
                    letterSpacing: calcRem(2.5),
                    marginBottom: calcRem(8),
                },
            },
        },
    ],
)

export const StyledPackagesContainer = styled.div<BreachTypeProps>(
    ({ breachType, useGridLayout, numberOfColumns }) => [
        // If useGridLayout is true and numberOfColumns is provided, use CSS Grid to layout the packages based on that number
        useGridLayout && numberOfColumns
            ? {
                  display: 'grid',
                  // On mobile and tablet, stack the packages vertically
                  gridTemplateColumns: `1fr`,
                  gridTemplateRows: `repeat(${numberOfColumns}, auto)`,
                  placeItems: 'center',
                  gap: calcRem(16), // 24 - 8px outline

                  // Not baseline widely available, but will help with tab order on supported browsers
                  readingFlow: 'grid-rows',

                  // On desktop, set the number of columns based on numberOfColumns prop
                  [breakpoint('lg')]: {
                      gridTemplateColumns: `repeat(${numberOfColumns}, 1fr)`,
                      gridTemplateRows: '1fr',
                      gap: calcRem(24), // 32 - 8px outline

                      // Not baseline widely available, but will help with tab order on supported browsers
                      readingFlow: 'grid-columns',
                  },
              }
            : {
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  alignSelf: 'stretch',
                  justifyContent: 'center',
              },
        // Shared properties for all breach types
        {
            position: 'relative',
            gap: calcRem(16, 12),
            width: '100%',
            marginTop: calcRem(36),

            [breakpoint('xs')]: {
                marginTop: 0,
            },
        },
        // PROMO: Digital Radio Package
        (breachType === 'digital-radio-and-papers' ||
            breachType === 'default-annual-package' ||
            breachType === 'summer-of-cricket') && {
            marginTop: 0,
        },
        // PROMO: Footy Lives Here
        breachType === 'afl-footy-lives-here-breach-screen' && {
            padding: calcRem(0, 16),

            [breakpoint('lg')]: {
                padding: calcRem(0, 24),
            },
        },
    ],
)
export const StyledFooterAndTermsContainer = styled.div<BreachTypeProps>(
    ({ breachType }) => [
        {
            marginTop: calcRem(16),
            display: 'flex',
            flexDirection: 'column',
            gap: calcRem(30),
            maxWidth: calcRem(636),
            width: '100%',
            alignItems: 'center',
            paddingBottom: calcRem(40),
        },
        // PROMO: Annual, Default Price Increase & Digital Radio Package
        (breachType === 'digital-radio-and-papers' ||
            breachType === 'default-annual-package' ||
            breachType === 'default-price-increase') && {
            marginTop: 0,
        },
        // PROMO: Digital Radio Package
        breachType === 'digital-radio-and-papers' && {
            paddingTop: calcRem(16),
            gap: calcRem(23),
            maxWidth: '100%',
            paddingBottom: 0,

            [breakpoint('sm')]: {
                paddingTop: 0,
            },
        },
        // PROMO: Summer of Cricket
        breachType === 'summer-of-cricket' && {
            paddingBottom: 0,
        },
        // PROMO: Footy lives here
        breachType === 'afl-footy-lives-here-breach-screen' && {
            padding: 0,
            margin: 0,
        },
    ],
)

export const StyledButton = styled('button')(({ theme }) => ({
    display: 'inline-block',
    border: `1px solid ${colors.white}`,
    padding: calcRem(theme.margins.sm, theme.margins.xl),
    marginTop: calcRem(theme.margins.lg),
    background: 'none',
    color: colors.white,
    borderRadius: calcRem(2),
    fontFamily: theme.fonts.sansSerif,
    fontWeight: 700,
    fontSize: calcRem(13),
    textDecoration: 'none',
    textTransform: 'uppercase',
    cursor: 'pointer',
    ...transition({ properties: ['background', 'color'] }),

    '&:hover': {
        backgroundColor: colors.white,
        color: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.blueKyanite,
            sevennews: colors.sevennews.red,
            fallback: undefined,
        }),
    },

    '&:focus': {
        backgroundColor: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
            fallback: undefined,
        }),
        borderColor: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
            fallback: undefined,
        }),
        color: 'currentColor',
        outline: 'none',
        textDecoration: 'underline',
    },
}))

export const StyledTerms = styled('p')(({ theme }) => ({
    color: colors.white,
    margin: 0,
    marginTop: calcRem(theme.margins.sm),
    fontSize: calcRem(11),
}))

export const StyledLiveTrailerWrap = styled('div')(({ theme }) => ({
    boxShadow: '0 0 16px rgba(0,0,0,0.5)',
    margin: `${calcRem(30)} auto`,
}))

export const StyledBlackFridayTape = styled('img')(() => ({
    display: 'flex',
    flexDirection: 'column',
    height: calcRem(24),
    position: 'absolute',

    [breakpoint('xs')]: {
        height: calcRem(52),
    },

    '&.left-tape': {
        top: 0,
        left: 0,
        transform: 'rotate(-12deg) translate(-9%, 0%)',

        [breakpoint('xs')]: {
            transform: 'rotate(-51deg) translate(-4.25%, -470%)',
        },
    },
    '&.right-tape': {
        top: 0,
        right: 0,
        transform: 'rotate(33deg) translate(40%, -200%)',

        [breakpoint('xs')]: {
            transform: 'rotate(18deg) translate(38%, -173%)',
        },
    },
    '&.bottom-left-tape': {
        bottom: 0,
        left: 0,
        visibility: 'hidden',

        [breakpoint('xs')]: {
            visibility: 'visible',
            transform: 'rotate(16deg) translate(-15.5%, 108%)',
        },
    },
    '&.bottom-right-tape': {
        bottom: 0,
        right: 0,
        width: '105%',
        transform: 'rotate(4deg) translate(2%, -70%)',

        [breakpoint('xs')]: {
            transform: 'rotate(-39deg) translate(20.5%, 342%)',
        },
    },
    '&.bottom-tape': {
        width: '105%',
        transform: 'rotate(5deg) translate(-0.5%, -70%)',

        // We don't want to show the bottom ribbon on anything other than
        // mobile version, so we do this to hide it
        [breakpoint('xs')]: {
            display: 'none',
        },

        bottom: 0,
    },
}))

export const StyledHelpMessage = styled('p')(({ theme }) => ({
    margin: calcRem(theme.margins.sm, 0),
}))

export const StyledTextLink = styled(TextLink)<{ reverseColor: boolean }>(
    ({ reverseColor }) => ({
        color: reverseColor ? colors.white : undefined,
    }),
)
